import { useNavigate } from "react-router-dom";
import { X } from "lucide-react";
import { useTranslation } from "@/shared/providers/LanguageProvider";
import { useNotifications } from "../NotificationProvider";
import { formatNotificationTime } from "../utils";
import { notificationTypeConfig } from "./notificationConfig";

type NotificationItemProps = {
  notification: Notification;
};

export function NotificationItem({ notification }: NotificationItemProps) {
  const { t, locale } = useTranslation();
  const navigate = useNavigate();
  const { markAsRead, removeNotification, closePanel } = useNotifications();
  const i18nContent = t.notifications.items[notification.itemKey];
  const content = notification.message
    ? {
        title: notification.title ?? i18nContent?.title ?? t.notifications.title,
        message: notification.message,
      }
    : (i18nContent ?? { title: t.notifications.title, message: notification.message ?? "" });
  const { icon: Icon, bg, color } = notificationTypeConfig[notification.type];

  const handleClick = () => {
    if (!notification.read) markAsRead(notification.id);
    if (notification.href) {
      closePanel();
      navigate(notification.href);
    }
  };

  return (
    <div
      className={`group relative flex gap-3 rounded-xl p-3 transition-colors hover:bg-accent/20 ${
        !notification.read ? "bg-primary-soft/40" : ""
      } ${notification.href ? "cursor-pointer" : ""}`}
    >
      <button type="button" onClick={handleClick} className="flex min-w-0 flex-1 gap-3 text-left">
        <div
          className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${bg}`}
        >
          <Icon size={18} className={color} strokeWidth={1.75} />
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <p className={`text-sm font-medium ${!notification.read ? "text-foreground" : "text-muted-foreground"}`}>
              {content.title}
            </p>
            {!notification.read && (
              <span className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-primary" />
            )}
          </div>
          <p className="mt-0.5 line-clamp-2 text-xs text-muted">{content.message}</p>
          <p className="mt-1 text-[11px] text-muted">
            {formatNotificationTime(notification.createdAt, t.notifications, locale)}
          </p>
        </div>
      </button>
      <button
        type="button"
        onClick={() => removeNotification(notification.id)}
        aria-label={t.notifications.delete}
        className="absolute right-2 top-2 flex h-6 w-6 items-center justify-center rounded-lg text-muted opacity-0 transition-opacity hover:bg-card hover:text-foreground group-hover:opacity-100"
      >
        <X size={14} />
      </button>
    </div>
  );
}
